
import React, { useState, useEffect, useRef } from 'react';

const Breakout: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState<'start' | 'playing' | 'over'>('start');

  useEffect(() => {
    if (status !== 'playing') return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;

    // HD DPI Scaling
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const cw = rect.width, ch = rect.height;

    const ballRadius = 10;
    let x = cw / 2;
    let y = ch - 50;
    let dx = 4;
    let dy = -4;
    const paddleHeight = 12;
    const paddleWidth = 100;
    let paddleX = (cw - paddleWidth) / 2;
    let rightPressed = false;
    let leftPressed = false;

    const brickRowCount = 5;
    const brickColumnCount = 7;
    const brickWidth = 75;
    const brickHeight = 24;
    const brickPadding = 8;
    const brickOffsetTop = 40;
    const brickOffsetLeft = (cw - (brickColumnCount * (brickWidth + brickPadding))) / 2;

    const bricks: any[] = [];
    for (let c = 0; c < brickColumnCount; c++) {
      bricks[c] = [];
      for (let r = 0; r < brickRowCount; r++) {
        bricks[c][r] = { x: 0, y: 0, status: 1, color: r % 2 === 0 ? '#ef4444' : '#f97316' };
      }
    }

    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === "Right" || e.key === "ArrowRight") rightPressed = true;
      else if (e.key === "Left" || e.key === "ArrowLeft") leftPressed = true;
    };
    const keyUpHandler = (e: KeyboardEvent) => {
      if (e.key === "Right" || e.key === "ArrowRight") rightPressed = false;
      else if (e.key === "Left" || e.key === "ArrowLeft") leftPressed = false;
    };
    const mouseMoveHandler = (e: MouseEvent) => {
      const relX = e.clientX - canvas.getBoundingClientRect().left;
      if (relX > 0 && relX < cw) paddleX = relX - paddleWidth / 2;
    };

    document.addEventListener("keydown", keyDownHandler);
    document.addEventListener("keyup", keyUpHandler);
    canvas.addEventListener("mousemove", mouseMoveHandler);

    const collisionDetection = () => {
      for (let c = 0; c < brickColumnCount; c++) {
        for (let r = 0; r < brickRowCount; r++) {
          const b = bricks[c][r];
          if (b.status === 1) {
            if (x > b.x && x < b.x + brickWidth && y > b.y && y < b.y + brickHeight) {
              dy = -dy;
              b.status = 0;
              setScore(s => s + 100);
              if (bricks.flat().every(bk => bk.status === 0)) setStatus('over');
            }
          }
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, cw, ch);
      
      // Draw Bricks (HD Neon)
      ctx.shadowBlur = 10;
      for (let c = 0; c < brickColumnCount; c++) {
        for (let r = 0; r < brickRowCount; r++) {
          if (bricks[c][r].status === 1) {
            const brickX = c * (brickWidth + brickPadding) + brickOffsetLeft;
            const brickY = r * (brickHeight + brickPadding) + brickOffsetTop;
            bricks[c][r].x = brickX;
            bricks[c][r].y = brickY;
            ctx.shadowColor = bricks[c][r].color;
            ctx.fillStyle = bricks[c][r].color;
            ctx.beginPath();
            ctx.roundRect(brickX, brickY, brickWidth, brickHeight, 4);
            ctx.fill();
          }
        }
      }

      // Draw Ball
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#3b82f6';
      ctx.fillStyle = "#3b82f6";
      ctx.beginPath();
      ctx.arc(x, y, ballRadius, 0, Math.PI * 2);
      ctx.fill();

      // Draw Paddle
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#fff';
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.roundRect(paddleX, ch - paddleHeight - 10, paddleWidth, paddleHeight, 6);
      ctx.fill();

      collisionDetection();

      if (x + dx > cw - ballRadius || x + dx < ballRadius) dx = -dx;
      if (y + dy < ballRadius) dy = -dy;
      else if (y + dy > ch - ballRadius - 10) {
        if (x > paddleX && x < paddleX + paddleWidth) {
          dy = -Math.abs(dy);
        } else if (y + dy > ch) {
          setStatus('over');
        }
      }

      if (rightPressed && paddleX < cw - paddleWidth) paddleX += 8;
      else if (leftPressed && paddleX > 0) paddleX -= 8;

      x += dx;
      y += dy;
      if (status === 'playing') requestAnimationFrame(draw);
    };

    draw();
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      document.removeEventListener("keyup", keyUpHandler);
      canvas.removeEventListener("mousemove", mouseMoveHandler);
    };
  }, [status]);

  return (
    <div className="flex flex-col items-center game-container">
      <div className="mb-6 flex justify-between w-full max-w-lg items-center glass p-4 rounded-2xl">
        <span className="text-xs font-black text-orange-500 tracking-widest uppercase">Firewall Breach</span>
        <span className="text-2xl font-black font-mono">SC: {score.toLocaleString()}</span>
      </div>
      
      <div className="relative group">
        <div className="absolute -inset-1 bg-orange-500/10 rounded-2xl blur-xl opacity-50"></div>
        <canvas 
          ref={canvasRef} 
          style={{ width: 'min(90vw, 600px)', height: '400px' }} 
          className="relative bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl" 
        />
      </div>

      {status !== 'playing' && (
        <button 
          onClick={() => { setStatus('playing'); setScore(0); }} 
          className="mt-8 bg-orange-600 hover:bg-orange-500 text-white px-12 py-4 rounded-2xl font-black shadow-xl shadow-orange-500/20 transition-all active:scale-95 uppercase tracking-tighter"
        >
          {status === 'over' ? 'RETRY BREACH' : 'BYPASS FIREWALL'}
        </button>
      )}
    </div>
  );
};

export default Breakout;
